import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form-normalize-on-blur/index';
import classnames from 'classnames';

import { uniqBy } from 'common/utils';
import { Button } from 'common/components';
import { isRequired } from 'common/form-validators';
import { trim } from 'common/normalizers';
import { close as Close, check as Check } from 'common/svg/icons';
import { Input } from '../';
import './index.css';

export class SelectComponent extends Component {
  state = { defaultValue: '', enableCreateMode: false, newOption: null };

  componentDidMount() {
    const { options, input } = this.props;
    const defaultOption = options.find(opt => opt.default);
    // set default value if default option is found
    // and current value is falsy (prevent it from overwritting existing value)
    if (!input.value && defaultOption && defaultOption.value) {
      this.setState({ defaultValue: defaultOption.value });
    }
  }

  renderOptions(options) {
    return options.map((item, idx) => (
      <option key={idx} value={item.value}>
        {item.label}
        {item.extra ? ` (${item.extra})` : ''}
      </option>
    ));
  }

  render() {
    const {
      input,
      meta,
      title,
      options,
      disablePromptOption = false,
      isRequired,
      theme,
      isCreatable,
      disabled,
      ...rest
    } = this.props;
    const { name, value } = input;
    const { touched, error } = meta;
    const { defaultValue, enableCreateMode, newOption } = this.state;

    const selectCSS = classnames(
      'select',
      touched && `select--${error ? 'invalid' : 'valid'}`,
      theme && theme.map(css => `select--${css}`).join(' ')
    );

    const selectedValue = value ? value : defaultValue;

    if (enableCreateMode) {
      return (
        <Input
          title={title}
          name={input.name}
          disabled={disabled}
          validateOnChange
          normalizeOnBlur={trim}
          withButton={
            <Fragment>
              <Button type="button" className="btn--no-border">
                <Close
                  width="16px"
                  height="16px"
                  onClick={this.handleCancelCreate}
                />
              </Button>
              <Button type="button" className="btn--no-border">
                <Check
                  width="16px"
                  height="16px"
                  onClick={this.handleConfirmCreate}
                />
              </Button>
            </Fragment>
          }
        />
      );
    }

    return (
      <fieldset data-testid="SelectComponent" className={'field'}>
        <label htmlFor={name}>
          {title}
          {isRequired ? ' *' : ''}
        </label>
        <div className={selectCSS}>
          <select
            id={name}
            disabled={disabled}
            {...rest}
            {...input}
            /**
             * don't pass `value` prop to <select /> when the value is falsy
             * otherwise, selecting an option will not update the label
             */
            {...(selectedValue ? { value: selectedValue } : {})}
            /**
             * overwrite onchange when creatable is true
             */
            {...(isCreatable ? { onChange: this.handleChange } : {})}
          >
            {!disablePromptOption && <option value="">Select {title}</option>}
            {this.renderOptions(
              newOption ? uniqBy([...options, newOption], 'value') : options
            )}
            {isCreatable && <option value="NEW_OPTION">Add new</option>}
          </select>
        </div>
        {touched && error && <small className={'text--error'}>{error}</small>}
      </fieldset>
    );
  }

  handleChange = e => {
    const { value } = e.target;
    const { onChange } = this.props.input;
    if (value === 'NEW_OPTION') {
      onChange('');
      this.setState({ enableCreateMode: true });
    } else {
      onChange(value);
    }
  };

  handleCancelCreate = () => {
    this.props.input.onChange('');
    this.setState({ enableCreateMode: false, newOption: null });
  };

  handleConfirmCreate = () => {
    const currentValue = this.props.input.value;
    this.setState({
      enableCreateMode: false,
      ...(currentValue
        ? { newOption: { label: currentValue, value: currentValue } }
        : {}),
    });
  };
}

SelectComponent.propTypes = {
  // added by redux-form field
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,

  // manually add
  component: PropTypes.func,
  className: PropTypes.string,
  title: PropTypes.string,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  disablePromptOption: PropTypes.bool,
  isRequired: PropTypes.bool,
  theme: PropTypes.array,
  isCreatable: PropTypes.bool,
};

SelectComponent.defaultProps = {
  component: null,
  className: '',
  title: '',
  disabled: false,
  disablePromptOption: false,
  theme: [],
  isCreatable: false,
};

const Select = props => {
  const { validate } = props;
  return (
    <Field
      {...props}
      validate={validate}
      component={SelectComponent}
      isRequired={isRequired(validate)}
    />
  );
};

Select.propTypes = {
  validate: PropTypes.arrayOf(PropTypes.func),
};

export default Select;



// WEBPACK FOOTER //
// ./src/common/components/Select/index.js